// Option sets for the factory request-quote form (RequestQuoteModal). Wording
// matches the approved mockup. `key` values are what persist to the DB
// (request_customization / requests); labels are trilingual for display.

const pick = (o, lang) => o[lang] || o.en;
export const labelFor = pick;

// "Does it need customization?" chips — multi-select. key → customization_types[].
export const CUSTOMIZATION_CHIPS = [
  { key: 'material_change', icon: 'diamond', ar: 'تعديل الخامة', en: 'Material change', zh: '材质更换' },
  { key: 'size_adjustment', icon: 'sliders', ar: 'تعديل المقاسات', en: 'Size adjustment', zh: '尺寸调整' },
  { key: 'custom_packaging', icon: 'box', ar: 'تغليف خاص', en: 'Custom packaging', zh: '定制包装' },
  { key: 'logo_printing', icon: 'stamp', ar: 'طباعة شعار', en: 'Logo printing', zh: 'Logo 印刷' },
  { key: 'other', icon: 'dots', ar: 'أخرى', en: 'Other', zh: '其他' },
];

// "Expected delivery" chips — single-select. key → request_customization.delivery_timeframe.
export const DELIVERY_TIMEFRAMES = [
  { key: '2_weeks', ar: 'خلال أسبوعين', en: 'Within 2 weeks', zh: '两周内' },
  { key: '1_month', ar: 'خلال شهر', en: 'Within a month', zh: '一个月内' },
  { key: '2_months', ar: 'خلال شهرين', en: 'Within 2 months', zh: '两个月内' },
  { key: 'flexible', ar: 'مرن', en: 'Flexible', zh: '灵活' },
];

// Quantity units → requests.unit.
export const REQUEST_UNITS = [
  { key: 'piece', ar: 'قطعة', en: 'Piece', zh: '件' },
  { key: 'set', ar: 'طقم', en: 'Set', zh: '套' },
  { key: 'carton', ar: 'كرتون', en: 'Carton', zh: '箱' },
  { key: 'pallet', ar: 'منصة', en: 'Pallet', zh: '托盘' },
  { key: 'container', ar: 'حاوية', en: 'Container', zh: '集装箱' },
  { key: 'meter', ar: 'متر', en: 'Meter', zh: '米' },
  { key: 'sqm', ar: 'متر مربع', en: 'm²', zh: '平方米' },
  { key: 'kg', ar: 'كجم', en: 'kg', zh: '公斤' },
  { key: 'ton', ar: 'طن', en: 'Ton', zh: '吨' },
];

// Shipping destination country → request_customization.ship_country (Saudi default).
export const SHIP_COUNTRIES = [
  { key: 'SA', flag: '🇸🇦', ar: 'السعودية', en: 'Saudi Arabia', zh: '沙特阿拉伯' },
  { key: 'AE', flag: '🇦🇪', ar: 'الإمارات', en: 'United Arab Emirates', zh: '阿联酋' },
  { key: 'KW', flag: '🇰🇼', ar: 'الكويت', en: 'Kuwait', zh: '科威特' },
  { key: 'QA', flag: '🇶🇦', ar: 'قطر', en: 'Qatar', zh: '卡塔尔' },
  { key: 'BH', flag: '🇧🇭', ar: 'البحرين', en: 'Bahrain', zh: '巴林' },
  { key: 'OM', flag: '🇴🇲', ar: 'عُمان', en: 'Oman', zh: '阿曼' },
  { key: 'other', flag: '🌍', ar: 'دولة أخرى', en: 'Other country', zh: '其他国家' },
];

// How the trader wants to receive the goods → requests.delivery_preference.
// CHECK constraint on the column accepts exactly these three keys
// (20260921122508); adding a fourth here without the migration fails the
// insert, and adding one to the column without a key here makes it unpickable.
//
// ⚠️ THIS IS NOT A COPY OF PRICE_BASES, and the two must not be merged.
// This is what the TRADER asks for, in their words. price_basis is what the
// SUPPLIER promises, in Incoterms. The mapping between them is a RANK, not an
// equivalence — see deliveryRank() in src/lib/deliveryMatch.js — because a
// quote that goes further than the trader asked is fine, and one that stops
// short is the thing worth flagging.
export const DELIVERY_PREFERENCES = [
  {
    key: 'door',
    ar: 'إلى باب عنواني', en: 'To my address', zh: '送到我的地址',
    hintAr: 'المورّد يوصّلها لين عندك', hintEn: 'The supplier delivers to you', hintZh: '供应商直接送到您那里',
  },
  {
    key: 'port',
    ar: 'إلى ميناء سعودي', en: 'To a Saudi port', zh: '送到沙特港口',
    hintAr: 'أنت تتكفّل بالتخليص والتوصيل من الميناء',
    hintEn: 'You handle customs and delivery from the port',
    hintZh: '由您负责清关和从港口的配送',
  },
  {
    key: 'factory',
    ar: 'أستلمها من المصنع', en: 'I collect from the factory', zh: '我自己到工厂提货',
    hintAr: 'عندك شركة شحن وتبي السعر من باب المصنع',
    hintEn: 'You have a freight forwarder and want the factory-door price',
    hintZh: '您有货代，想要工厂交货价',
  },
];

export const deliveryHintFor = (o, lang) =>
  (lang === 'ar' ? o.hintAr : lang === 'zh' ? o.hintZh : o.hintEn);

// Sample requirement → requests.sample_requirement. Lifted out of the JSX in
// Requests.jsx (2026-09-21) so the three VALUES have one home and a test can
// hold them still; the trader page renders them and nothing else writes them.
//
// ORDER IS DISPLAY ORDER AND IT IS DELIBERATE: strongest ask first, matching
// the mobile app. The radio list this replaced ran the other way (none →
// preferred → required). Reordering is safe precisely because there is no
// default — `sample_requirement` starts as '' and the submit REQUIRES a
// choice, so nothing is selected by position.
//
// `zh` ADDED 2026-09-27 — the owner's content decision (every request picker
// trilingual). It used to be absent on purpose, pending exactly that decision.
// The strings are the ones IdeaToProduct.jsx already showed for the same three
// values, so no reader meets a second wording. Render with labelFor(). The
// direct form in Requests.jsx still renders `isAr ? ar : en`, so this addition
// does not change what it shows.
export const SAMPLE_REQUIREMENTS = [
  { key: 'required', ar: 'عينة إلزامية', en: 'Sample is mandatory', zh: '必须提供样品' },
  { key: 'preferred', ar: 'عينة مفضلة إن توفرت', en: 'Sample preferred if available', zh: '如方便可提供样品' },
  { key: 'none', ar: 'لا حاجة لعينة', en: 'No sample needed', zh: '无需样品' },
];

/**
 * A stored `requests.ship_city` rendered in the reader's language.
 *
 * The column holds the ENGLISH name in every language — deliberately, because
 * the value travels to a Chinese factory and an Arabic string tells that reader
 * nothing (see the picker in Requests.jsx). The consequence nobody had handled
 * is the Arabic reader on the other side of the same row: their delivery line
 * read «يوصّلها لين بابك في Riyadh», one Latin word dropped into an Arabic
 * sentence.
 *
 * This is the KSA_CITIES list read backwards — the same list the picker wrote
 * the value from, so a label cannot drift from a value we store. NOT
 * src/lib/cityNames.js, which solves the opposite problem: free-text ARABIC
 * `profiles.city` turned into English for a factory, with an unmapped value
 * hidden rather than passed through.
 *
 * zh gets the English name. KSA_CITIES carries no Chinese, and English is what
 * the rest of the product shows a Chinese reader for a Saudi city.
 *
 * Fallback is the RAW value: ship_city is only usually from the picker, and a
 * city we cannot map is still better shown than swallowed — the trader typed
 * it and the factory ships there.
 */
export const cityLabel = (city, lang) => {
  const raw = String(city || '').trim();
  if (!raw) return '';
  const hit = KSA_CITIES.find(
    (c) => c.en.toLowerCase() === raw.toLowerCase() || c.ar === raw,
  );
  if (!hit) return raw;
  return lang === 'ar' ? hit.ar : hit.en;
};

// KSA cities (dropdown when country = SA; free text otherwise).
export const KSA_CITIES = [
  // The first twenty are the original list, unchanged and in their order:
  // their `en` is what existing requests stored, so it must never change.
  { ar: 'الرياض', en: 'Riyadh' },
  { ar: 'جدة', en: 'Jeddah' },
  { ar: 'مكة المكرمة', en: 'Mecca' },
  { ar: 'المدينة المنورة', en: 'Medina' },
  { ar: 'الدمام', en: 'Dammam' },
  { ar: 'الخبر', en: 'Khobar' },
  { ar: 'الظهران', en: 'Dhahran' },
  { ar: 'الأحساء', en: 'Al-Ahsa' },
  { ar: 'الجبيل', en: 'Jubail' },
  { ar: 'الطائف', en: 'Taif' },
  { ar: 'بريدة', en: 'Buraidah' },
  { ar: 'تبوك', en: 'Tabuk' },
  { ar: 'خميس مشيط', en: 'Khamis Mushait' },
  { ar: 'أبها', en: 'Abha' },
  { ar: 'حائل', en: 'Hail' },
  { ar: 'نجران', en: 'Najran' },
  { ar: 'جازان', en: 'Jazan' },
  { ar: 'ينبع', en: 'Yanbu' },
  { ar: 'عرعر', en: 'Arar' },
  { ar: 'سكاكا', en: 'Sakaka' },
  // Added 2026-10-25 (owner: «يظهر جميع اسماء المدن»): the other cities and
  // governorates, region by region.
  // Riyadh region
  { ar: 'الخرج', en: 'Al-Kharj' },
  { ar: 'الدرعية', en: 'Diriyah' },
  { ar: 'المجمعة', en: 'Al Majmaah' },
  { ar: 'الزلفي', en: 'Az Zulfi' },
  { ar: 'شقراء', en: 'Shaqra' },
  { ar: 'الدوادمي', en: 'Ad Dawadmi' },
  { ar: 'القويعية', en: 'Al Quwayiyah' },
  { ar: 'وادي الدواسر', en: 'Wadi ad-Dawasir' },
  { ar: 'الأفلاج', en: 'Al Aflaj' },
  { ar: 'السليل', en: 'As Sulayyil' },
  { ar: 'عفيف', en: 'Afif' },
  { ar: 'حوطة بني تميم', en: 'Hotat Bani Tamim' },
  { ar: 'الحريق', en: 'Al Hariq' },
  { ar: 'المزاحمية', en: 'Al Muzahimiyah' },
  { ar: 'ضرما', en: 'Dhurma' },
  { ar: 'رماح', en: 'Rumah' },
  { ar: 'ثادق', en: 'Thadiq' },
  { ar: 'حريملاء', en: 'Huraymila' },
  { ar: 'الغاط', en: 'Al Ghat' },
  { ar: 'مرات', en: 'Marat' },
  { ar: 'حوطة سدير', en: 'Hotat Sudair' },
  // Makkah region
  { ar: 'رابغ', en: 'Rabigh' },
  { ar: 'القنفذة', en: 'Al Qunfudhah' },
  { ar: 'الليث', en: 'Al Lith' },
  { ar: 'الجموم', en: 'Al Jumum' },
  { ar: 'خليص', en: 'Khulais' },
  { ar: 'الكامل', en: 'Al Kamil' },
  { ar: 'تربة', en: 'Turabah' },
  { ar: 'رنية', en: 'Ranyah' },
  { ar: 'الخرمة', en: 'Al Khurmah' },
  { ar: 'أضم', en: 'Adham' },
  { ar: 'المويه', en: 'Al Muwayh' },
  { ar: 'ميسان', en: 'Maysan' },
  { ar: 'بحرة', en: 'Bahrah' },
  // Madinah region
  { ar: 'العلا', en: 'AlUla' },
  { ar: 'بدر', en: 'Badr' },
  { ar: 'خيبر', en: 'Khaybar' },
  { ar: 'الحناكية', en: 'Al Hinakiyah' },
  { ar: 'المهد', en: 'Mahd adh Dhahab' },
  { ar: 'وادي الفرع', en: 'Wadi al-Fara' },
  // Eastern Province
  { ar: 'الهفوف', en: 'Hofuf' },
  { ar: 'المبرز', en: 'Al Mubarraz' },
  { ar: 'القطيف', en: 'Qatif' },
  { ar: 'سيهات', en: 'Saihat' },
  { ar: 'صفوى', en: 'Safwa' },
  { ar: 'رأس تنورة', en: 'Ras Tanura' },
  { ar: 'بقيق', en: 'Abqaiq' },
  { ar: 'حفر الباطن', en: 'Hafar Al-Batin' },
  { ar: 'الخفجي', en: 'Khafji' },
  { ar: 'النعيرية', en: 'An Nairyah' },
  { ar: 'قرية العليا', en: 'Qaryat al Ulya' },
  // Qassim region
  { ar: 'القصيم', en: 'Al-Qassim' },
  { ar: 'عنيزة', en: 'Unaizah' },
  { ar: 'الرس', en: 'Ar Rass' },
  { ar: 'المذنب', en: 'Al Mithnab' },
  { ar: 'البكيرية', en: 'Al Bukayriyah' },
  { ar: 'البدائع', en: 'Al Badai' },
  { ar: 'رياض الخبراء', en: 'Riyadh Al Khabra' },
  { ar: 'عيون الجواء', en: 'Uyun al Jiwa' },
  { ar: 'الشماسية', en: 'Ash Shimasiyah' },
  { ar: 'النبهانية', en: 'An Nabhaniyah' },
  { ar: 'الأسياح', en: 'Al Asyah' },
  // Hail region
  { ar: 'بقعاء', en: 'Baqaa' },
  { ar: 'الغزالة', en: 'Al Ghazalah' },
  { ar: 'الشنان', en: 'Ash Shinan' },
  // Tabuk region
  { ar: 'تيماء', en: 'Tayma' },
  { ar: 'ضباء', en: 'Duba' },
  { ar: 'الوجه', en: 'Al Wajh' },
  { ar: 'أملج', en: 'Umluj' },
  { ar: 'حقل', en: 'Haql' },
  // Northern Borders
  { ar: 'رفحاء', en: 'Rafha' },
  { ar: 'طريف', en: 'Turaif' },
  { ar: 'العويقيلة', en: 'Al Uwayqilah' },
  // Al-Jouf
  { ar: 'دومة الجندل', en: 'Dumat al-Jandal' },
  { ar: 'القريات', en: 'Al Qurayyat' },
  { ar: 'طبرجل', en: 'Tabarjal' },
  // Asir
  { ar: 'بيشة', en: 'Bisha' },
  { ar: 'محايل عسير', en: 'Muhayil Asir' },
  { ar: 'النماص', en: 'An Namas' },
  { ar: 'أحد رفيدة', en: 'Ahad Rafidah' },
  { ar: 'سراة عبيدة', en: 'Sarat Abidah' },
  { ar: 'رجال ألمع', en: 'Rijal Alma' },
  { ar: 'المجاردة', en: 'Al Majardah' },
  { ar: 'تثليث', en: 'Tathlith' },
  { ar: 'بلقرن', en: 'Balqarn' },
  { ar: 'ظهران الجنوب', en: 'Dhahran Al Janub' },
  // Jazan region
  { ar: 'صبيا', en: 'Sabya' },
  { ar: 'أبو عريش', en: 'Abu Arish' },
  { ar: 'صامطة', en: 'Samtah' },
  { ar: 'بيش', en: 'Baish' },
  { ar: 'أحد المسارحة', en: 'Ahad al Masarihah' },
  { ar: 'الدرب', en: 'Ad Darb' },
  { ar: 'فرسان', en: 'Farasan' },
  // Najran region
  { ar: 'شرورة', en: 'Sharurah' },
  { ar: 'حبونا', en: 'Hubuna' },
  { ar: 'يدمة', en: 'Yadamah' },
  // Al-Baha region
  { ar: 'الباحة', en: 'Al Baha' },
  { ar: 'بلجرشي', en: 'Baljurashi' },
  { ar: 'المندق', en: 'Al Mandaq' },
  { ar: 'المخواة', en: 'Al Makhwah' },
  { ar: 'العقيق', en: 'Al Aqiq' },
  { ar: 'قلوة', en: 'Qilwah' },
];

// Arabic search folding: hamza forms → ا, ة → ه, ى → ي, no tatweel or
// diacritics, and a leading ال dropped — so «المدينه», «مدينة» and «Medina»
// all find المدينة المنورة.
export function foldCityName(s) {
  return String(s || '').toLowerCase().trim()
    .replace(/[ً-ٰٟـ]/g, '')
    .replace(/[أإآٱ]/g, 'ا').replace(/ة/g, 'ه').replace(/ى/g, 'ي')
    .replace(/^ال/, '').replace(/[\s\-']+/g, ' ');
}

/** The cities matching `query` (Arabic or English), in list order. */
export function searchCities(query) {
  const q = foldCityName(query);
  if (!q) return KSA_CITIES;
  return KSA_CITIES.filter((c) => foldCityName(c.ar).includes(q) || foldCityName(c.en).includes(q) || foldCityName(c.ar.replace(/^ال/, '')).includes(q));
}

// Attachment limits (mirror the private request-attachments bucket).
export const ATTACH_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
export const ATTACH_MAX_COUNT = 6;
export const ATTACH_ACCEPT = 'image/*,application/pdf,.zip,.ai,.eps';
